import type { TaskTemplate, TaskTemplateInput } from "@/schema/task-template"

import { ApiContext, apiContextFetch } from "@/hooks/api/base"

export function fetchCompanyTaskTemplates(context: ApiContext, options?: { scope?: "company" | "book" }) {
  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ task_templates: TaskTemplate[] }>(context, `task_templates${params}`)
}

async function uploadFiles(context: ApiContext, files: File[]): Promise<string[]> {
  const apiRoot = (process.env.NEXT_PUBLIC_API_URL || "http://localhost:3000/api/").replace("/api/", "")
  const { uploadFileDirectly } = await import("@/utils/direct-upload")

  return Promise.all(files.map((file) => uploadFileDirectly(file, apiRoot, context.token)))
}

export async function createCompanyTaskTemplate(
  context: ApiContext,
  taskTemplate: TaskTemplateInput,
  options?: { scope?: "company" | "book" }
) {
  const { files, copy_from_task, ...templateParams } = taskTemplate

  const fileSignedIds = files?.length ? await uploadFiles(context, files) : []

  const params = options?.scope ? `?scope=${options.scope}` : ""
  return apiContextFetch<{ task_template: TaskTemplate }>(context, `task_templates${params}`, {
    method: "POST",
    body: JSON.stringify({
      task_template: {
        ...templateParams,
        ...(fileSignedIds.length > 0 && { files: fileSignedIds }),
      },
      ...(copy_from_task && { copy_from_task }),
    }),
  })
}

export async function updateCompanyTaskTemplate(
  context: ApiContext,
  id: string,
  taskTemplate: Partial<TaskTemplateInput>
) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { files, copy_from_task, existing_signed_ids, ...templateParams } = taskTemplate

  const newSignedIds = files?.length ? await uploadFiles(context, files) : []
  const allFileSignedIds = [...(existing_signed_ids || []), ...newSignedIds]

  return apiContextFetch<{ task_template: TaskTemplate }>(context, `task_templates/${id}`, {
    method: "PATCH",
    body: JSON.stringify({
      task_template: {
        ...templateParams,
        files: allFileSignedIds,
      },
    }),
  })
}

export function destroyCompanyTaskTemplate(context: ApiContext, id: string) {
  return apiContextFetch<void>(context, `task_templates/${id}`, {
    method: "DELETE",
  })
}

export function cloneCompanyTaskTemplate(context: ApiContext, id: string, bookId?: string) {
  return apiContextFetch<{ task_template: TaskTemplate }>(context, `task_templates/${id}/clone`, {
    method: "POST",
    body: JSON.stringify({ clone: { ...(bookId && { book_id: bookId }) } }),
  })
}
